import React from 'react';
import { ShieldCheck, DollarSign, Clock, Award, Headphones, ThumbsUp } from 'lucide-react';

const WhyUsSection = () => {
  const scrollToForm = () => {
    const element = document.getElementById('lead-form');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const features = [
    {
      icon: ShieldCheck,
      title: 'Licensed & Insured',
      description:
        'Fully licensed, bonded, and insured technicians. Your home and family are protected on every job.',
    },
    {
      icon: DollarSign,
      title: 'Upfront Pricing',
      description:
        'No hidden fees or surprise charges. We provide detailed quotes before starting any work.',
    },
    {
      icon: Clock,
      title: '24/7 Availability',
      description:
        'HVAC emergencies don\'t wait, and neither do we. Available around the clock for urgent repairs.',
    },
    {
      icon: Award,
      title: 'Certified Technicians',
      description:
        'NATE-certified professionals with ongoing training to stay current with the latest HVAC technology.',
    },
    {
      icon: Headphones,
      title: 'Dedicated Support',
      description:
        'Our customer service team is here to help with scheduling, questions, and follow-up care.',
    },
    {
      icon: ThumbsUp,
      title: 'Satisfaction Guarantee',
      description:
        'Not happy with our work? We\'ll make it right. Your satisfaction is our top priority.',
    },
  ];

  return (
    <section
      id="why-us"
      data-testid="why-us-section"
      className="py-20 md:py-32 bg-[#F8FAFC]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <p className="text-sm font-medium tracking-wide uppercase text-[#0EA5E9] mb-4">
            Why Choose Us
          </p>
          <h2
            data-testid="why-us-headline"
            className="text-4xl md:text-5xl font-bold text-[#0F172A] mb-6"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Why Choose Our Network
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed">
            We connect you with vetted, licensed HVAC professionals. Here's why thousands 
            of homeowners trust our network.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {features.map((feature, index) => (
            <div
              key={index}
              data-testid={`why-us-feature-${index}`}
              className="bg-white p-8 rounded-2xl border border-slate-100 hover:shadow-lg transition-all duration-300 group"
            >
              <div className="w-12 h-12 bg-[#E0F2FE] rounded-xl flex items-center justify-center mb-5 group-hover:bg-[#0EA5E9] transition-colors duration-300">
                <feature.icon className="w-6 h-6 text-[#0EA5E9] group-hover:text-white transition-colors duration-300" />
              </div>

              <h3
                className="text-xl font-bold text-[#0F172A] mb-3"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                {feature.title}
              </h3>

              <p className="text-slate-600 leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>

        {/* Call to Action Banner */}
        <div
          data-testid="why-us-cta-banner"
          className="mt-16 bg-[#0F172A] rounded-3xl p-8 md:p-12 text-center"
        >
          <h3
            className="text-2xl md:text-3xl font-bold text-white mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Ready to Find Your Local Pro?
          </h3>
          <p className="text-slate-300 mb-6 max-w-2xl mx-auto">
            Join over 5,000 homeowners who found trusted HVAC professionals through our network.
          </p>
          <button
            onClick={scrollToForm}
            data-testid="why-us-phone-cta"
            className="inline-flex items-center gap-2 bg-[#F97316] hover:bg-[#EA580C] text-white font-bold py-4 px-8 rounded-full transition-all duration-300 transform hover:-translate-y-1 orange-glow cursor-pointer"
          >
            <Headphones className="w-5 h-5" />
            Get Your Free Quote
          </button>
        </div>
      </div>
    </section>
  );
};

export default WhyUsSection;
